<template>
	<div class="mall-shop-page">
		<div v-loading="loading" class="goods-publish-page">
			<div class="goods-step-bar" :class="'step-count-' + stepList.length">
				<div class="goods-step-track"></div>
				<div v-for="(item, index) in stepList" :key="item" class="goods-step" :class="{ active: index === 0 }">
					<div class="step-circle">{{ index + 1 }}</div>
					<div class="step-text">{{ item }}</div>
				</div>
			</div>

			<el-form
				ref="goodsFormRef"
				:model="formData"
				label-position="right"
				label-width="126px"
				class="goods-publish-form"
				:disabled="isReadonly"
			>
				<div class="goods-form-card">
					<div class="goods-main-column">
						<div class="publish-section-title">基础信息</div>
						<el-form-item label="上架类型：" required>
							<el-radio-group v-model="formData.goodsType">
								<el-radio :label="1">平台自营商品</el-radio>
								<el-radio :label="2">展销商品</el-radio>
							</el-radio-group>
						</el-form-item>
						<el-form-item label="商品标题：" required>
							<el-input v-model="formData.goodsTitle" maxlength="60" show-word-limit placeholder="请输入商品标题" clearable />
						</el-form-item>
						<el-form-item label="商品类目：" required>
							<el-cascader
								v-model="categoryValue"
								:options="categoryOptions"
								:props="{ value: 'id', label: 'name', children: 'children' }"
								filterable
								clearable
								placeholder="请输入相关关键词搜索"
								class="full-control"
								@change="handleCategoryChange"
							/>
						</el-form-item>
						<el-form-item label="导购短标题：" required>
							<el-input v-model="formData.guideTitle" maxlength="24" show-word-limit placeholder="请输入导购短标题" clearable />
						</el-form-item>
					</div>

					<div class="goods-help-column">
						<help-card title="商品标题">
							<li>建议 16-60 个字符，突出商品核心卖点。</li>
							<li>不要出现夸张、误导、违规或与商品无关的信息。</li>
							<li>避免特殊符号堆叠，保持清晰可读。</li>
						</help-card>
						<help-card title="导购短标题">
							<li>建议 6-12 个汉字，表达商品亮点。</li>
							<li>与商品标题保持一致，不重复堆砌关键词。</li>
						</help-card>
					</div>
				</div>

				<div class="goods-form-card">
					<div class="goods-main-column">
						<div class="publish-section-title">商品售价</div>
						<template v-if="Number(formData.goodsType) === GOODS_TYPE.SELF">
							<el-form-item label="售价：" required>
								<div class="inline-controls">
									<el-input v-model="formData.sellingPrice" placeholder="请填写价格" class="price-input" clearable />
									<el-select v-model="formData.priceUnit" class="small-select">
										<el-option label="元" value="元" />
									</el-select>
									<span class="separator">/</span>
									<el-select v-model="formData.measureUnit" class="small-select">
										<el-option v-for="item in measureUnitOptions" :key="item" :label="item" :value="item" />
									</el-select>
								</div>
							</el-form-item>
						</template>
						<template v-else>
							<el-form-item label="售价：">
								<el-radio-group v-model="formData.priceType">
									<el-radio :label="2">面议</el-radio>
								</el-radio-group>
							</el-form-item>
							<el-form-item label="联系人：" required>
								<el-input v-model="formData.contactName" placeholder="自动带入店铺信息，支持手动填写" clearable />
							</el-form-item>
							<el-form-item label="联系方式：" required>
								<el-input v-model="formData.contactPhone" placeholder="自动带入店铺信息，支持手动填写" clearable />
							</el-form-item>
						</template>

						<el-form-item label="发货时间：" required>
							<el-radio-group v-model="formData.deliveryType">
								<el-radio :label="1">当日发货</el-radio>
								<el-radio :label="2">24小时</el-radio>
								<el-radio :label="3">48小时</el-radio>
								<el-radio :label="4">自定义</el-radio>
							</el-radio-group>
							<el-input
								v-if="Number(formData.deliveryType) === 4"
								v-model="formData.deliveryCustom"
								placeholder="请输入自定义发货时间"
								class="delivery-custom"
								clearable
							/>
						</el-form-item>
						<el-form-item label="发货约定：">
							<el-input v-model="formData.deliveryAgreement" type="textarea" :rows="4" resize="none" />
						</el-form-item>
					</div>

					<div class="goods-help-column">
						<help-card title="商品主图">
							<li>最少上传 1 张，最多 10 张，建议上传 3 张以上。</li>
							<li>建议 1:1 或 3:4 图片比例，主体清晰完整。</li>
							<li>仅支持 jpg/jpeg/png，单张图片不超过 5MB。</li>
						</help-card>
					</div>
				</div>

				<div class="goods-form-card">
					<div class="goods-main-column full-width">
						<div class="publish-section-title">商品简介</div>
						<el-form-item label="商品主图：" required>
							<upload-drag-images
								v-model="mainImageFiles"
								:action="uploadAction"
								:data="mainImageUploadData"
								:headers="uploadHeaders"
								:limit="10"
								:size="5"
								accept=".jpg,.jpeg,.png"
								:preview-url="previewUrl"
								:delete-api="deleteApi"
								:delete-params="mainImageUploadData"
								:disabled="isReadonly"
							/>
						</el-form-item>
						<el-form-item label="商品详情描述：" required>
							<UEditor
								v-model="formData.goodsDetail"
								:height="360"
								:maximum-words="20000"
								placeholder="请在这里编辑商品详情，支持文字格式化、直接粘贴、插入本地图片..."
								:disabled="isReadonly"
							/>
						</el-form-item>
					</div>
				</div>

				<div v-if="Number(formData.goodsType) === GOODS_TYPE.EXHIBITION" class="goods-form-card">
					<div class="goods-main-column full-width">
						<div class="publish-section-title">支付展销费</div>
						<div class="slot-location-card">
							<h4 class="slot-location-title">
								<span>展销费套餐</span>
								选择后商品将获得展销曝光权益
							</h4>
							<div v-if="slotPackages.length" class="slot-package-list">
								<div
									v-for="item in slotPackages"
									:key="item.id || item.itemPackageId"
									class="slot-package-card"
									:class="{ active: selectedSlotPackageId === getPackageKey(item), disabled: isReadonly }"
									@click="handleSelectSlotPackage(item)"
								>
									<div v-if="item.tag" class="tag-label">{{ item.tag }}</div>
									<h5 class="slot-duration">{{ formatDuration(item.longDuration) }}</h5>
									<div class="slot-price-box">
										<p class="slot-now-price">
											<span>¥</span>
											{{ item.eventPrice || 0 }}
										</p>
										<p v-if="item.packagePrice && item.packagePrice !== item.eventPrice" class="slot-old-price">
											<span>¥</span>
											{{ item.packagePrice }}
										</p>
									</div>
								</div>
							</div>
							<empty-content v-else empty-text="暂无展销费套餐"></empty-content>
						</div>
					</div>
				</div>

				<div v-if="!isReadonly" class="goods-submit-actions">
					<el-button :loading="draftLoading" @click="handleSaveDraft">保存草稿</el-button>
					<el-button type="primary" :loading="submitLoading" @click="handleSubmit">提交商品</el-button>
				</div>
				<div v-else class="goods-submit-actions">
					<el-button type="primary" @click="$router.push('/mall/shop/goods-manage')">返回商品管理</el-button>
				</div>
			</el-form>
		</div>
	</div>
</template>

<script>
	import EmptyContent from '@/components/EmptyContent'
	import UEditor from '@/components/UEditor'
	import UploadDragImages from '@/components/UploadDragImages/index.vue'
	import {
		deleteMerchantFile,
		previewMerchantFile,
		queryMerchantFileList,
		queryMerchantGoodsDetail,
		queryMerchantOrderPrice,
		queryMyShop,
		saveMerchantGoodsDraft,
		submitMerchantGoods,
		uploadMerchantFile
	} from '@/api/mall'
	import { getUuid } from '@/utils'
	import { getToken } from '@/utils/auth'
	import { mapGetters } from 'vuex'

	const GOODS_TYPE = {
		SELF: 1,
		EXHIBITION: 2
	}

	export default {
		name: 'MallGoodsPublish',
		components: {
			EmptyContent,
			UEditor,
			UploadDragImages,
			HelpCard: {
				functional: true,
				props: { title: String },
				render(h, context) {
					return h('div', { class: 'goods-help-card' }, [
						h('div', { class: 'help-title' }, [h('i', { class: 'el-icon-info' }), context.props.title]),
						h('ul', context.children)
					])
				}
			}
		},
		data() {
			return {
				GOODS_TYPE,
				loading: false,
				draftLoading: false,
				submitLoading: false,
				shopInfo: {},
				categoryValue: [],
				mainImageFiles: [],
				slotPackages: [],
				selectedSlotPackageId: '',
				uploadAction: uploadMerchantFile(),
				previewUrl: previewMerchantFile(),
				deleteApi: deleteMerchantFile,
				uploadHeaders: {
					InternetToken: getToken()
				},
				measureUnitOptions: ['件', '套', '项', '次', '个'],
				categoryOptions: [
					{
						id: 'digital-service',
						name: '数智产品',
						children: [
							{
								id: 'software',
								name: '软件服务',
								children: [
									{ id: 'custom-dev', name: '定制开发' },
									{ id: 'system-build', name: '系统建设' }
								]
							},
							{
								id: 'data',
								name: '数据服务',
								children: [
									{ id: 'data-screen', name: '可视化大屏' },
									{ id: 'data-governance', name: '数据治理' }
								]
							}
						]
					},
					{
						id: 'construction-service',
						name: '工程服务',
						children: [
							{
								id: 'consulting',
								name: '咨询服务',
								children: [
									{ id: 'qualification-consulting', name: '资质咨询' },
									{ id: 'policy-consulting', name: '政策咨询' }
								]
							},
							{
								id: 'material',
								name: '材料服务',
								children: [
									{ id: 'material-prepare', name: '材料编制' },
									{ id: 'material-review', name: '材料审核' }
								]
							}
						]
					}
				],
				formData: {
					id: '',
					goodsType: GOODS_TYPE.EXHIBITION,
					goodsTitle: '',
					guideTitle: '',
					categoryFirstId: '',
					categoryFirstName: '',
					categorySecondId: '',
					categorySecondName: '',
					categoryThirdId: '',
					categoryThirdName: '',
					priceType: 2,
					sellingPrice: '',
					sellingPriceId: '',
					priceUnit: '元',
					measureUnit: '件',
					purchaseTime: '',
					purchaseTimeId: '',
					contactName: '',
					contactPhone: '',
					deliveryType: 1,
					deliveryCustom: '',
					deliveryAgreement: '',
					mainImage: '',
					goodsDetail: '',
					eventId: '',
					packageId: '',
					purchasePrice: '',
					rowState: 7
				}
			}
		},
		computed: {
			...mapGetters(['userId']),
			stepList() {
				const steps = ['基础信息', '图文信息', '价格信息']
				if (Number(this.formData.goodsType) === GOODS_TYPE.EXHIBITION) {
					steps.push('支付展销费')
				}
				return steps
			},
			mainImageUploadData() {
				return {
					businessId: this.formData.id,
					fileBusinessType: 'GOODS_MAIN_IMAGE'
				}
			},
			isReadonly() {
				return String(this.$route.query.readonly || '') === '1'
			}
		},
		watch: {
			'formData.goodsType'(value) {
				this.formData.priceType = Number(value) === GOODS_TYPE.SELF ? 1 : 2
				if (Number(value) === GOODS_TYPE.EXHIBITION) {
					this.formData.sellingPrice = ''
				} else {
					this.selectedSlotPackageId = ''
					this.formData.eventId = ''
					this.formData.packageId = ''
					this.formData.purchaseTime = ''
					this.formData.purchaseTimeId = ''
					this.formData.purchasePrice = ''
					this.formData.sellingPriceId = ''
				}
			}
		},
		created() {
			this.formData.id = this.$route.query.id || getUuid()
			this.initPage()
		},
		methods: {
			async initPage() {
				this.loading = true
				try {
					await this.loadShop()
					await this.loadSlotPackages()
					if (this.$route.query.id) {
						await this.loadGoodsDetail(this.$route.query.id)
					}
				} finally {
					this.loading = false
				}
			},
			async loadShop() {
				const res = await queryMyShop().catch(() => ({ data: null }))
				this.shopInfo = res.data || {}
				if (!this.shopInfo.id || Number(this.shopInfo.shopState) !== 1) {
					this.$message.warning('只有正常经营的店铺才能发布商品')
					this.$router.replace('/mall/shop/my')
					return
				}
				if (!this.formData.contactName) {
					this.formData.contactName = this.shopInfo.contactPerson || this.shopInfo.contactName || ''
				}
				if (!this.formData.contactPhone) {
					this.formData.contactPhone = this.shopInfo.contactPhone || this.shopInfo.telephone || ''
				}
			},
			async loadSlotPackages() {
				const res = await queryMerchantOrderPrice({ packageTypeList: 500, userId: this.userId }).catch(() => ({ data: [] }))
				const list = Array.isArray(res.data)
					? res.data
					: Object.keys(res.data || {}).reduce((result, key) => result.concat(res.data[key] || []), [])
				this.slotPackages = list.sort((a, b) => Number(b.longDuration || 0) - Number(a.longDuration || 0))
			},
			async loadGoodsDetail(id) {
				const res = await queryMerchantGoodsDetail({ id })
				const goods = res.data || {}
				const detail = goods.merchantGoods || goods.goods || goods
				this.formData = {
					...this.formData,
					...detail,
					id: detail.id || id,
					goodsType: Number(detail.goodsType || this.formData.goodsType),
					deliveryType: Number(detail.deliveryType || 1),
					priceType: Number(detail.priceType || (Number(detail.goodsType) === GOODS_TYPE.SELF ? 1 : 2))
				}
				this.categoryValue = [detail.categoryFirstId, detail.categorySecondId, detail.categoryThirdId].filter(Boolean)
				this.selectedSlotPackageId = detail.packageId || detail.purchaseTimeId || detail.sellingPriceId || ''
				this.mainImageFiles = this.normalizeFiles(goods.merchantFiles || goods.fileList || [])
				if (!this.mainImageFiles.length) {
					await this.loadFiles(this.formData.id)
				}
			},
			async loadFiles(businessId) {
				const res = await queryMerchantFileList({ businessId }).catch(() => ({ data: [] }))
				this.mainImageFiles = this.normalizeFiles(res.data).filter(item => item.fileBusinessType === 'GOODS_MAIN_IMAGE')
			},
			normalizeFiles(files) {
				if (!Array.isArray(files)) return []
				return files.map(item => {
					const fileId = item.id || item.fileId || item.fileRecordId
					return {
						fileId,
						fileBusinessType: item.fileBusinessType,
						url: fileId ? previewMerchantFile() + fileId : item.url
					}
				})
			},
			handleCategoryChange(value) {
				const nodes = this.findCategoryNodes(value)
				this.formData.categoryFirstId = nodes[0] ? nodes[0].id : ''
				this.formData.categoryFirstName = nodes[0] ? nodes[0].name : ''
				this.formData.categorySecondId = nodes[1] ? nodes[1].id : ''
				this.formData.categorySecondName = nodes[1] ? nodes[1].name : ''
				this.formData.categoryThirdId = nodes[2] ? nodes[2].id : ''
				this.formData.categoryThirdName = nodes[2] ? nodes[2].name : ''
			},
			findCategoryNodes(value) {
				if (!Array.isArray(value)) return []
				const nodes = []
				let list = this.categoryOptions
				value.forEach(id => {
					const node = list.find(item => item.id === id)
					if (node) {
						nodes.push(node)
						list = node.children || []
					}
				})
				return nodes
			},
			getPackageKey(item) {
				return item.itemPackageId || item.id || item.itemEventPromotionId
			},
			getPackagePrice(item) {
				if (!item) return ''
				if (item.eventPrice !== undefined && item.eventPrice !== null && item.eventPrice !== '') return item.eventPrice
				if (item.packagePrice !== undefined && item.packagePrice !== null && item.packagePrice !== '') return item.packagePrice
				return 0
			},
			getSelectedPackagePrice() {
				const item = this.slotPackages.find(
					packageItem => String(this.getPackageKey(packageItem)) === String(this.selectedSlotPackageId)
				)
				return this.getPackagePrice(item)
			},
			handleSelectSlotPackage(item) {
				if (this.isReadonly) return
				this.selectedSlotPackageId = this.getPackageKey(item)
				this.formData.eventId = item.itemEventPromotionId
				this.formData.packageId = item.itemPackageId
				this.formData.purchaseTime = item.longDuration
				this.formData.purchaseTimeId = item.itemPackageId
				this.formData.sellingPriceId = item.id || item.itemPackageId
				this.formData.purchasePrice = this.getPackagePrice(item)
			},
			formatDuration(days) {
				const daysNum = Number(days || 0)
				if (!daysNum) return '按套餐时长'
				if (daysNum >= 365) return `${Math.floor(daysNum / 365)}年`
				if (daysNum >= 30) return `${Math.ceil(daysNum / 30)}个月`
				return `${daysNum}天`
			},
			validateSubmit() {
				if (!this.formData.goodsTitle) return '请输入商品标题'
				if (!this.formData.categoryThirdId) return '请选择商品类目'
				if (!this.formData.guideTitle) return '请输入导购短标题'
				if (!this.formData.deliveryType) return '请选择发货时间'
				if (Number(this.formData.deliveryType) === 4 && !this.formData.deliveryCustom) return '请输入自定义发货时间'
				if (!this.mainImageFiles.length) return '请上传商品主图'
				if (!this.formData.goodsDetail || this.formData.goodsDetail === '<p><br/></p>') return '请输入商品详情描述'
				if (Number(this.formData.goodsType) === GOODS_TYPE.SELF && !this.formData.sellingPrice) return '请填写商品售价'
				if (Number(this.formData.goodsType) === GOODS_TYPE.EXHIBITION) {
					if (!this.formData.contactName) return '请输入联系人'
					if (!this.formData.contactPhone) return '请输入联系方式'
					if (!this.formData.packageId) return '请选择展销费套餐'
				}
				return ''
			},
			getSubmitData(rowState) {
				const mainFile = this.mainImageFiles[0] || {}
				const isExhibition = Number(this.formData.goodsType) === GOODS_TYPE.EXHIBITION
				const data = {
					...this.formData,
					priceType: isExhibition ? 2 : 1,
					mainImage: mainFile.fileId || this.formData.mainImage || '',
					purchasePrice: isExhibition
						? this.formData.purchasePrice !== undefined &&
						  this.formData.purchasePrice !== null &&
						  this.formData.purchasePrice !== ''
							? this.formData.purchasePrice
							: this.getSelectedPackagePrice()
						: '',
					rowState
				}
				if (!isExhibition) {
					Object.assign(data, {
						eventId: '',
						packageId: '',
						purchaseTime: '',
						purchaseTimeId: '',
						purchasePrice: '',
						sellingPriceId: ''
					})
				}
				return data
			},
			async handleSaveDraft() {
				this.draftLoading = true
				try {
					await saveMerchantGoodsDraft(this.getSubmitData(7))
					this.$message.success('草稿已保存')
					this.$router.push('/mall/shop/goods-manage')
				} finally {
					this.draftLoading = false
				}
			},
			async handleSubmit() {
				const message = this.validateSubmit()
				if (message) {
					this.$message.warning(message)
					return
				}
				this.submitLoading = true
				try {
					await submitMerchantGoods(this.getSubmitData(1))
					this.$message.success('商品已提交，请等待审核')
					this.$router.push('/mall/shop/goods-manage')
				} finally {
					this.submitLoading = false
				}
			}
		}
	}
</script>

<style lang="scss" scoped>
	@import './shop.scss';
</style>
